import { useCallback, useEffect, useRef, useState } from 'react';
import { generateCallToken, isPreviousCallClosingError } from '@/services/calls/client';
import type { CallCredentials, GenerateCallTokenRequest } from '@/services/calls/types';

/**
 * `waiting`: the backend refused the token because the previous call with this
 * client is still closing; the hook is retrying on its own and the screen
 * should show that (with a way out) rather than an error.
 */
export type CallTokenStatus = 'loading' | 'waiting' | 'success' | 'error';

interface UseCallTokenResult {
    status: CallTokenStatus;
    credentials: CallCredentials | null;
    /** The backend's message: the refusal being retried while `waiting`, the failure on `error`. */
    error: string | null;
    /** Re-request a token (e.g. after a failure). */
    retry: () => void;
}

/**
 * Back-off between token attempts while the previous call is still closing.
 * The backend releases the client when LiveKit's `room_finished` webhook lands,
 * typically 20–60 s after the last hang-up, so keep trying for ~30 s before
 * handing the guide a manual Retry. A refused request creates no call on the
 * backend, so retrying is free for the client.
 */
const PREVIOUS_CALL_RETRY_DELAYS_MS = [3000, 5000, 7000, 7000, 8000];

/**
 * Fetches LiveKit room credentials for a call and tracks request state.
 *
 * Thin React layer over [generateCallToken] — keeps the token fetch decoupled
 * from the in-call UI. Fetches once per (channel, channel_id, phone_id) — a
 * different picked number is a different call, so it refetches; the LiveKit
 * server URL comes from `ENV.LIVEKIT_URL`, not from this response.
 */
export function useCallToken(params: GenerateCallTokenRequest): UseCallTokenResult {
    const [status, setStatus] = useState<CallTokenStatus>('loading');
    const [credentials, setCredentials] = useState<CallCredentials | null>(null);
    const [error, setError] = useState<string | null>(null);

    const { channel, channel_id, phone_id } = params;
    const mountedRef = useRef(true);
    // Each fetchToken() call is a new run; a run that finds itself stale (a
    // newer run started, or the hook unmounted) drops its result silently.
    const runRef = useRef(0);

    const fetchToken = useCallback(async () => {
        const run = ++runRef.current;
        const stale = () => !mountedRef.current || runRef.current !== run;

        setStatus('loading');
        setError(null);
        // A refetch is a new call: drop the old room so nothing (e.g. a
        // terminate on page hide) can act on credentials that no longer apply.
        setCredentials(null);
        if (!channel || !channel_id) {
            setError('This conversation can’t be called right now.');
            setStatus('error');
            return;
        }
        for (let attempt = 0; ; attempt++) {
            try {
                const creds = await generateCallToken({ channel, channel_id, phone_id });
                if (stale()) return;
                setCredentials(creds);
                setStatus('success');
                return;
            } catch (e: any) {
                if (stale()) return;
                const delay = PREVIOUS_CALL_RETRY_DELAYS_MS[attempt];
                if (delay === undefined || !isPreviousCallClosingError(e)) {
                    setError(e?.message || 'Could not start the call. Please try again.');
                    setStatus('error');
                    return;
                }
                setError(e.message);
                setStatus('waiting');
                await new Promise((resolve) => setTimeout(resolve, delay));
                if (stale()) return;
            }
        }
    }, [channel, channel_id, phone_id]);

    useEffect(() => {
        mountedRef.current = true;
        fetchToken();
        return () => {
            mountedRef.current = false;
        };
    }, [fetchToken]);

    return { status, credentials, error, retry: fetchToken };
}
